import React, { useMemo } from 'react';
import { Box, Typography, Divider, Tooltip, tooltipClasses } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';

import EventTimelineBarChart from 'components/EventTimelineBarChart';
import CircularLoader from 'components/CircularLoader';

import icn_linebar_chart from 'assets/images/icons/icn_linebar_chart.png';
import { ReactComponent as Clock } from 'assets/images/icons/icn_display_clock.svg';
import icn_info_solid from 'assets/images/icons/icn_Info_solid.svg';

const HtmlTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#4D4D4D',
    color: '#FFFFFF',
    maxWidth: 550,
    fontSize: theme.typography.pxToRem(12),
    padding: '16px 20px',
    borderRadius: '8px',
    lineHeight: '1.5',
    boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.3)',
  },
}));

const TooltipContent = () => (
  <Box>
    <Box sx={{ mb: 2.5 }}>
      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>Good — Cloud Connection Success Rate &gt; 99%</Typography>
      <Typography variant="body2" sx={{ fontSize: '12px' }}>
        Devices are maintaining a stable connection to the cloud management platform.
      </Typography>
    </Box>

    <Box sx={{ mb: 2.5 }}>
      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>Warning — 95% ≤ Cloud Connection Success Rate &lt; 99%</Typography>
      <Typography variant="body2" sx={{ fontSize: '12px', mb: 1 }}>
        Some devices have lost their management link. Status updates or configuration sync may be delayed.
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>Recommended Actions:</Typography>
      <Box component="ul" sx={{ pl: 2, m: 0, fontSize: '12px' }}>
        <li>Verify DNS reachability and check edge firewall load.</li>
      </Box>
    </Box>

    <Box>
      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>Critical — Cloud Connection Success Rate &lt; 95%</Typography>
      <Typography variant="body2" sx={{ fontSize: '12px', mb: 1 }}>
        Devices are dropping off the cloud platform and may be running standalone, blocking remote management.
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>Recommended Actions:</Typography>
      <Box component="ul" sx={{ pl: 2, m: 0, fontSize: '12px' }}>
        <li>Audit the upstream network path and MTU settings.</li>
        <li>Confirm the latest cloud IP ranges and ports are white-listed.</li>
      </Box>
    </Box>
  </Box>
);

const UplinkStabilityStatus = ({ data, loading, isEmpty, onClick }) => {
  const theme = useTheme();
  const handleOpenDrawer = !isEmpty ? onClick : undefined;

  const getStatus = (unlink, all) => {
    if (all === 0) return { label: 'Good', color: '#4CAF50' };
    const successRate = (all - unlink) / all;
    if (successRate >= 0.99) return { label: 'Good', color: '#4CAF50' };
    if (successRate >= 0.95) return { label: 'Warning', color: '#FF9800' };
    return { label: 'Critical', color: '#F44336' };
  };

  const status = data ? getStatus(data.unlinkDevice, data.allOnlineDevice) : { label: 'Good', color: '#4CAF50' };
  const affectedDevices = data ? `${data.unlinkDevice}` : '0';
  const lossEvents = data && data.series ? data.series.reduce((acc, curr) => acc + curr[1], 0) : 0;

  const barChartData = useMemo(() => {
    if (!data) return null;
    const now = new Date();
    const startHourTs = new Date(now).setMinutes(0, 0, 0, 0) - 23 * 60 * 60 * 1000;

    return {
      series: [
        {
          name: 'Loss Events',
          data: data.series
            .map(d => {
              // 確保將 UTC 字串正確轉為 Local Timestamp
              const ts = typeof d[0] === 'string' && !d[0].endsWith('Z') && !d[0].includes('+')
                ? new Date(d[0] + 'Z').getTime()
                : new Date(d[0]).getTime();
              return [ts, d[1]];
            })
            .filter(d => d[0] >= startHourTs)
        }
      ]
    };
  }, [data]);

  return (
    <Box
      sx={{
        border: '1px solid #E2E8F0',
        borderRadius: '12px',
        p: 2,
        bgcolor: 'white',
        mb: 3,
        cursor: isEmpty ? 'default' : 'pointer',
        '&:hover': handleOpenDrawer ? {
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)',
        } : {}
      }}
      onClick={handleOpenDrawer}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box
            component="img"
            src={icn_linebar_chart}
            sx={{ width: '18px', height: '18px', mr: 1 }}
          />
          <Typography sx={{ fontWeight: 'bold', color: '#172664', fontSize: '15px', mr: 1 }}>
            Uplink Stability Status
          </Typography>
          <HtmlTooltip title={<TooltipContent />} placement="auto">
            <Box
              component="img"
              src={icn_info_solid}
              sx={{ width: '16px', height: '16px', cursor: 'pointer' }}
            />
          </HtmlTooltip>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            color: theme.palette.primary.main,
          }}
        >
          <Clock />
          <Typography
            sx={{
              marginLeft: '5px',
              fontSize: theme.typography.customFontSize[2],
              fontWeight: theme.typography.customFontWeight[2],
            }}
          >
            Last 24 Hours
          </Typography>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ height: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularLoader />
        </Box>
      ) : isEmpty ? (
        <Box sx={{ height: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography sx={{ color: theme.palette.primary.greyThird, fontSize: theme.typography.customFontSize[2] }}>
            No data available
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
          <Box sx={{ width: { xs: '100%', md: '25%' }, pr: { xs: 0, md: 4 }, mb: { xs: 3, md: 0 } }}>
            <Typography sx={{ color: '#1C1C1E', fontWeight: 'bold', fontSize: '13px', mb: 1 }}>Device-to-platform Connectivity Status</Typography>
            <Typography sx={{ fontSize: '35px', fontWeight: 'bold', color: status.color, lineHeight: 1.2, mb: 2 }}>
              {status.label === 'Warning' ? 'Warn' : status.label}
            </Typography>

            <Divider sx={{ mb: 2 }} />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Typography sx={{ fontSize: '13px', fontWeight: 'bold', color: '#172664' }}>Affected Device(s):</Typography>
                <Typography sx={{ fontSize: '13px', color: '#4C6EF5', fontWeight: 'bold' }}>{affectedDevices}</Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Typography sx={{ fontSize: '13px', fontWeight: 'bold', color: '#172664' }}>Loss Event(s):</Typography>
                <Typography sx={{ fontSize: '13px', color: '#4C6EF5', fontWeight: 'bold' }}>{lossEvents}</Typography>
              </Box>
            </Box>
          </Box>

          <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' }, mr: 4, borderColor: '#EDF2F7' }} />

          <Box sx={{ flex: 1, width: '100%' }}>
            <EventTimelineBarChart barChartData={barChartData} height="200px" />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default UplinkStabilityStatus;